import { Box, Button, HStack, Input, Switch, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Play, Save, Settings, Square } from 'lucide-react'
import { useEffect, useState } from 'react'
import { mqttProfilesApi } from '@/lib/api'
import type {
  ConnectionFormState,
  ConnectionStatus,
  DEFAULT_PORTS,
  MqttProtocol,
  MqttQoS,
  MqttVersion,
} from '@/lib/mqtt-types'
import { useColorModeValue } from '~/color-mode'
import { ProfileManagerDrawer } from './ProfileManagerDrawer'

const DEFAULT_PORTS_MAP: Record<MqttProtocol, number> = {
  mqtt: 1883,
  mqtts: 8883,
  ws: 8080,
  wss: 8884,
}

interface ConnectionPanelProps {
  status: ConnectionStatus
  onConnect: (config: ConnectionFormState, profileId?: string) => void
  onDisconnect: () => void
  onSaveProfile?: (config: ConnectionFormState) => void
}

export function ConnectionPanel({
  status,
  onConnect,
  onDisconnect,
  onSaveProfile,
}: ConnectionPanelProps) {
  const [selectedProfileId, setSelectedProfileId] = useState<string>('')
  const [isProfileManagerOpen, setIsProfileManagerOpen] = useState(false)
  const [profileHasPassword, setProfileHasPassword] = useState(false)
  const selectBorderColor = useColorModeValue('#CDCCC9', '#5F5D5A')
  const warningColor = useColorModeValue('#D16503', '#FB8A3B')

  const [form, setForm] = useState<ConnectionFormState>({
    protocol: 'mqtts',
    host: '',
    port: 8883,
    path: '/mqtt',
    username: '',
    password: '',
    clientIdPrefix: 'stories2.0',
    tlsInsecure: false,
    mqttVersion: 5,
    cleanSession: true,
    keepAlive: 60,
    maxMessages: 1000,
    lwTopic: '',
    lwMessage: '',
    lwQos: 0,
    lwRetain: false,
  })

  // Fetch profiles
  const { data: profilesData } = useQuery({
    queryKey: ['mqtt-profiles'],
    queryFn: () => mqttProfilesApi.list(),
  })

  const profiles = profilesData?.profiles || []

  // Load selected profile
  useEffect(() => {
    if (selectedProfileId) {
      const profile = profiles.find((p) => p.id === selectedProfileId)
      if (profile) {
        // If profile has username, it likely has a saved password
        setProfileHasPassword(!!profile.username)
        setForm({
          protocol: profile.protocol as MqttProtocol,
          host: profile.host,
          port: profile.port,
          path: profile.path || '/mqtt',
          username: profile.username || '',
          password: '', // Password is fetched from backend when connecting
          clientIdPrefix: profile.clientIdPrefix || 'stories',
          tlsInsecure: profile.tlsInsecure,
          mqttVersion: profile.mqttVersion as MqttVersion,
          cleanSession: profile.cleanSession,
          keepAlive: profile.keepAlive,
          maxMessages: profile.maxMessages || 1000,
          lwTopic: profile.lwTopic || '',
          lwMessage: profile.lwMessage || '',
          lwQos: profile.lwQos as MqttQoS,
          lwRetain: profile.lwRetain,
        })
      }
    } else {
      setProfileHasPassword(false)
    }
  }, [selectedProfileId, profiles])

  const handleProtocolChange = (protocol: MqttProtocol) => {
    setForm((prev) => ({
      ...prev,
      protocol,
      port: DEFAULT_PORTS_MAP[protocol],
    }))
  }

  const handleConnect = () => {
    // Pass profileId if a saved profile is selected (so parent can fetch password)
    onConnect(form, selectedProfileId || undefined)
  }

  const isConnected = status === 'connected'
  const isConnecting = status === 'connecting'

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" bg="bg.panel">
      <Text fontWeight="bold" mb={4}>
        Connection
      </Text>

      <VStack gap={3} align="stretch">
        {/* Profile Selector */}
        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="sm">Profile</Text>
            {profiles.length > 0 && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setIsProfileManagerOpen(true)}
                title="Manage profiles"
              >
                <Settings size={14} />
              </Button>
            )}
          </HStack>
          {profiles.length > 0 ? (
            <select
              value={selectedProfileId}
              onChange={(e) => setSelectedProfileId(e.target.value)}
              disabled={isConnected || isConnecting}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: `1px solid ${selectBorderColor}`,
                backgroundColor: 'white',
              }}
            >
              <option value="">Select a profile...</option>
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.isPublic ? '🌐 ' : '🔒 '}
                  {profile.name}
                </option>
              ))}
            </select>
          ) : (
            <Text fontSize="xs" color="content.secondary">
              No saved profiles. Connect and save to create one.
            </Text>
          )}
        </Box>

        {/* Protocol */}
        <Box>
          <Text fontSize="sm" mb={1}>
            Protocol
          </Text>
          <HStack gap={1}>
            {(['mqtts', 'mqtt', 'wss', 'ws'] as MqttProtocol[]).map((protocol) => (
              <Button
                key={protocol}
                size="sm"
                variant={form.protocol === protocol ? 'solid' : 'outline'}
                colorPalette={form.protocol === protocol ? 'brand' : undefined}
                onClick={() => handleProtocolChange(protocol)}
                disabled={isConnected || isConnecting}
              >
                {protocol.toUpperCase()}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Host & Port */}
        <HStack gap={2}>
          <Box flex={3}>
            <Text fontSize="sm" mb={1}>
              Host
            </Text>
            <Input
              size="sm"
              placeholder="broker.hivemq.com"
              value={form.host}
              onChange={(e) => setForm((prev) => ({ ...prev, host: e.target.value }))}
              disabled={isConnected || isConnecting}
            />
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" mb={1}>
              Port
            </Text>
            <Input
              size="sm"
              type="number"
              value={form.port}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, port: parseInt(e.target.value) || 0 }))
              }
              disabled={isConnected || isConnecting}
            />
          </Box>
        </HStack>

        {/* Path (for WebSocket) */}
        {(form.protocol === 'ws' || form.protocol === 'wss') && (
          <Box>
            <Text fontSize="sm" mb={1}>
              Path
            </Text>
            <Input
              size="sm"
              placeholder="/mqtt"
              value={form.path}
              onChange={(e) => setForm((prev) => ({ ...prev, path: e.target.value }))}
              disabled={isConnected || isConnecting}
            />
          </Box>
        )}

        {/* Username & Password */}
        <HStack gap={2}>
          <Box flex={1}>
            <Text fontSize="sm" mb={1}>
              Username
            </Text>
            <Input
              size="sm"
              placeholder="Optional"
              value={form.username}
              onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
              disabled={isConnected || isConnecting}
            />
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" mb={1}>
              Password
            </Text>
            <Input
              size="sm"
              type="password"
              placeholder={selectedProfileId && profileHasPassword ? '••••••••' : 'Optional'}
              value={form.password}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, password: e.target.value }))
                // If user types a password, they're overriding the saved one
                if (e.target.value) {
                  setProfileHasPassword(false)
                }
              }}
              disabled={isConnected || isConnecting}
            />
          </Box>
        </HStack>

        {/* TLS Insecure Warning */}
        {(form.protocol === 'mqtts' || form.protocol === 'wss') && (
          <HStack justify="space-between">
            <HStack gap={2}>
              {form.tlsInsecure && <AlertTriangle size={14} color={warningColor} />}
              <Text fontSize="sm">Ignore Certificate Errors</Text>
            </HStack>
            <Switch.Root
              checked={form.tlsInsecure}
              onCheckedChange={(e) => setForm((prev) => ({ ...prev, tlsInsecure: e.checked }))}
              disabled={isConnected || isConnecting}
            >
              <Switch.HiddenInput />
              <Switch.Control />
            </Switch.Root>
          </HStack>
        )}

        {/* Client ID Prefix */}
        <Box>
          <Text fontSize="sm" mb={1}>
            Client ID Prefix
          </Text>
          <Input
            size="sm"
            placeholder="stories"
            value={form.clientIdPrefix}
            onChange={(e) => setForm((prev) => ({ ...prev, clientIdPrefix: e.target.value }))}
            disabled={isConnected || isConnecting}
          />
        </Box>

        {/* MQTT Version */}
        <Box>
          <Text fontSize="sm" mb={1}>
            MQTT Version
          </Text>
          <HStack gap={1}>
            {([5, 3] as MqttVersion[]).map((version) => (
              <Button
                key={version}
                size="sm"
                variant={form.mqttVersion === version ? 'solid' : 'outline'}
                colorPalette={form.mqttVersion === version ? 'brand' : undefined}
                onClick={() => setForm((prev) => ({ ...prev, mqttVersion: version }))}
                disabled={isConnected || isConnecting}
              >
                MQTT {version === 5 ? '5.0' : '3.1.1'}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Clean Session & Keep Alive */}
        <HStack gap={4}>
          <HStack>
            <Text fontSize="sm">Clean Session</Text>
            <Switch.Root
              checked={form.cleanSession}
              onCheckedChange={(e) => setForm((prev) => ({ ...prev, cleanSession: e.checked }))}
              disabled={isConnected || isConnecting}
            >
              <Switch.HiddenInput />
              <Switch.Control />
            </Switch.Root>
          </HStack>
          <Box flex={1}>
            <Text fontSize="sm" mb={1}>
              Keep Alive (s)
            </Text>
            <Input
              size="sm"
              type="number"
              value={form.keepAlive}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, keepAlive: parseInt(e.target.value) || 60 }))
              }
              disabled={isConnected || isConnecting}
            />
          </Box>
        </HStack>

        {/* Max Messages */}
        <Box>
          <Text fontSize="sm" mb={1}>
            Max Cached Messages
          </Text>
          <Input
            size="sm"
            type="number"
            value={form.maxMessages}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, maxMessages: parseInt(e.target.value) || 1000 }))
            }
            disabled={isConnected || isConnecting}
          />
        </Box>

        {/* Last Will */}
        <Box borderTopWidth="1px" pt={3}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Last Will & Testament
          </Text>
          <VStack gap={2} align="stretch">
            <Input
              size="sm"
              placeholder="LW Topic"
              value={form.lwTopic}
              onChange={(e) => setForm((prev) => ({ ...prev, lwTopic: e.target.value }))}
              disabled={isConnected || isConnecting}
            />
            <Input
              size="sm"
              placeholder="LW Message"
              value={form.lwMessage}
              onChange={(e) => setForm((prev) => ({ ...prev, lwMessage: e.target.value }))}
              disabled={isConnected || isConnecting}
            />
            <HStack gap={2}>
              <Box flex={1}>
                <Text fontSize="xs" mb={1}>
                  QoS
                </Text>
                <HStack gap={1}>
                  {([0, 1, 2] as MqttQoS[]).map((qos) => (
                    <Button
                      key={qos}
                      size="xs"
                      variant={form.lwQos === qos ? 'solid' : 'outline'}
                      colorPalette={form.lwQos === qos ? 'brand' : undefined}
                      onClick={() => setForm((prev) => ({ ...prev, lwQos: qos }))}
                      disabled={isConnected || isConnecting}
                    >
                      {qos}
                    </Button>
                  ))}
                </HStack>
              </Box>
              <HStack>
                <Text fontSize="xs">Retain</Text>
                <Switch.Root
                  checked={form.lwRetain}
                  onCheckedChange={(e) => setForm((prev) => ({ ...prev, lwRetain: e.checked }))}
                  disabled={isConnected || isConnecting}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {/* Action Buttons */}
        <HStack gap={2} pt={2}>
          {!isConnected ? (
            <Button
              colorPalette="yellow"
              onClick={handleConnect}
              loading={isConnecting}
              disabled={!form.host}
              flex={1}
            >
              <Play size={16} />
              Connect
            </Button>
          ) : (
            <Button colorPalette="red" variant="outline" onClick={onDisconnect} flex={1}>
              <Square size={16} />
              Disconnect
            </Button>
          )}

          {onSaveProfile && !isConnected && (
            <Button
              variant="outline"
              onClick={() => onSaveProfile(form)}
              disabled={!form.host}
              title="Save as profile"
            >
              <Save size={16} />
            </Button>
          )}
        </HStack>
      </VStack>

      {/* Profile Manager Drawer */}
      <ProfileManagerDrawer
        isOpen={isProfileManagerOpen}
        onClose={() => setIsProfileManagerOpen(false)}
      />
    </Box>
  )
}

export default ConnectionPanel
